import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import api from "../../lib/api";
import { API } from "../../App";
import Sidebar from "../../components/Sidebar";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, TrendUp } from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

export default function PipelineVelocityReport({ user, logout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [bdeId, setBdeId] = useState("");
  const [bdes, setBdes] = useState([]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("requester_role", user?.role || "");
      if (dateFrom) params.set("date_from", new Date(dateFrom).toISOString());
      if (dateTo) params.set("date_to", new Date(dateTo).toISOString());
      if (bdeId) params.set("bde_id", bdeId);
      const r = await axios.get(`${API}/reports/pipeline-velocity?${params}`);
      setData(r.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Failed to load" });
    } finally { setLoading(false); }
  }, [user?.role, dateFrom, dateTo, bdeId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  useEffect(() => {
    api.get(`/users`).then(r => {
      setBdes((r.data || []).filter(u => u.role === "bde"));
    }).catch(() => {});
  }, []);

  const stages = data?.stages || [];
  const funnel = data?.funnel || [];
  const palette = ["#6366f1", "#10b981", "#f59e0b", "#ec4899"];

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="px-8 pt-8 pb-6 max-w-6xl">
          <Link to="/reports-hub" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3" data-testid="back-to-hub">
            <ArrowLeft size={14} /> Reports Hub
          </Link>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight" data-testid="pipeline-velocity-title">
            Pipeline Conversion Velocity
          </h1>
          <p className="text-slate-600 text-sm mt-1">Avg days at each stage transition. Lower is faster.</p>
        </div>

        <div className="px-8 pb-12 max-w-6xl space-y-6">
          {/* Filters */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                     className="px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="filter-date-from" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                     className="px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="filter-date-to" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">BDE</label>
              <select value={bdeId} onChange={e => setBdeId(e.target.value)}
                      className="px-3 py-2 border border-slate-300 rounded-md text-sm min-w-[180px]" data-testid="filter-bde">
                <option value="">All BDEs</option>
                {bdes.map(b => <option key={b.user_id} value={b.user_id}>{b.name}</option>)}
              </select>
            </div>
            <button onClick={() => { setDateFrom(""); setDateTo(""); setBdeId(""); }}
                    className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900" data-testid="filter-reset">
              Clear
            </button>
          </div>

          {loading ? (
            <div className="text-center text-slate-400 py-16">Loading…</div>
          ) : data?.error ? (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-6">{data.error}</div>
          ) : (
            <>
              {/* Stage cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="stage-cards">
                {stages.map((s, i) => (
                  <div key={s.key} className="bg-white border border-slate-200 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock size={16} weight="bold" style={{ color: palette[i] }} />
                      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{s.name}</div>
                    </div>
                    <div className="text-3xl font-black text-slate-900">{s.avg_days}<span className="text-base font-bold text-slate-400 ml-1">days avg</span></div>
                    <div className="text-xs text-slate-500 mt-2">{s.count} transitions · range {s.min_days}–{s.max_days}d</div>
                  </div>
                ))}
              </div>

              {/* Funnel chart */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <TrendUp size={18} weight="bold" className="text-indigo-600" />
                  <h3 className="font-bold text-slate-900">Pipeline Funnel</h3>
                </div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={funnel} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                    <YAxis type="category" dataKey="stage" stroke="#475569" fontSize={12} width={120} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                      {funnel.map((_, i) => <Cell key={i} fill={palette[i % palette.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm text-indigo-800">
                <span className="font-bold">Overall Lead → SO:</span> ~{data?.overall_avg_days_lead_to_so} days end-to-end
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
